import React, { useMemo, useState } from "react";
import { Check, X, ArrowRight, MessageSquare } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Footer from "@/components/landing/Footer";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import Faq from "@/components/landing/Faq";
import FinalCTA from "@/components/landing/FinalCTA";
import Seo from "@/components/Seo";

// ---------------------------------------------------------------------------
// Reusable building blocks for cornerstone comparison-guide pages.
// All variation lives in the config passed to <CornerstonePage />.
// ---------------------------------------------------------------------------

export const Pill = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-1 text-xs text-[hsl(var(--ink-soft))]">
    {Icon && <Icon className="h-3 w-3" strokeWidth={1.8} />}
    {label}
  </span>
);

export const SectionHeader = ({ eyebrow, title, lead }) => (
  <div className="max-w-3xl">
    {eyebrow && (
      <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
        {eyebrow}
      </p>
    )}
    <h2 className="mt-4 font-display text-3xl font-medium leading-[1.1] text-[hsl(var(--ink))] sm:text-4xl">
      {title}
    </h2>
    {lead && (
      <p className="mt-4 text-base text-[hsl(var(--ink-soft))] sm:text-lg">{lead}</p>
    )}
  </div>
);

export const CheckCell = ({ ok }) =>
  ok ? (
    <Check className="mx-auto h-4 w-4 text-[hsl(var(--forest))]" strokeWidth={2.4} />
  ) : (
    <X className="mx-auto h-4 w-4 text-[hsl(var(--ink-soft))]/40" strokeWidth={2} />
  );

export const ComparisonTable = ({ competitors, rows, note, featureLabel = "Functie" }) => (
  <div
    data-testid="comparison-table"
    className="mt-10 overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]"
  >
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]">
            <th className="sticky left-0 z-10 bg-[hsl(var(--surface-secondary))] px-4 py-3 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ink-soft))]">
              {featureLabel}
            </th>
            {competitors.map((c, i) => (
              <th
                key={c}
                className={`px-3 py-3 text-center text-[11px] uppercase tracking-[0.14em] ${
                  i === 0
                    ? "font-semibold text-[hsl(var(--forest))]"
                    : "text-[hsl(var(--ink-soft))]"
                }`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-b border-[hsl(var(--border))] last:border-0">
              <td className="sticky left-0 z-10 bg-[hsl(var(--surface))] px-4 py-3 text-[hsl(var(--ink))]">
                {row.label}
              </td>
              {row.values.map((v, i) => (
                <td key={i} className={`px-3 py-3 ${i === 0 ? "bg-[hsl(var(--forest))]/5" : ""}`}>
                  <CheckCell ok={v} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {note && (
      <p className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] px-4 py-3 text-xs text-[hsl(var(--ink-soft))]">
        {note}
      </p>
    )}
  </div>
);

export const DecisionHelper = ({ questions, recommend, ui }) => {
  const [answers, setAnswers] = useState({});
  const total = questions.length;
  const answered = Object.keys(answers).length;
  const score = useMemo(
    () => Object.values(answers).reduce((s, v) => s + v, 0),
    [answers]
  );
  const maxScore = questions.reduce(
    (s, q) => s + Math.max(...q.options.map((o) => o.score)),
    0
  );
  const recommendation = useMemo(() => {
    if (answered < total) return null;
    return recommend(score / maxScore);
  }, [answered, total, score, maxScore, recommend]);

  const qLabel = (qi) =>
    ui?.questionLabel
      ? ui.questionLabel.replace("{{current}}", qi + 1).replace("{{total}}", total)
      : `Vraag ${qi + 1} van ${total}`;
  const remaining = total - answered;
  const prompt =
    remaining === 1
      ? (ui?.answerPrompt || "Beantwoord de {{count}} resterende vraag om een aanbeveling te zien.").replace("{{count}}", remaining)
      : (ui?.answerPromptPlural || "Beantwoord de {{count}} resterende vragen om een aanbeveling te zien.").replace("{{count}}", remaining);
  const resultEyebrow = ui?.resultEyebrow || "Jouw resultaat";

  return (
    <div data-testid="decision-helper" className="mt-10 space-y-8">
      {questions.map((q, qi) => (
        <div key={q.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6">
          <p className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--sand-dark))]">
            {qLabel(qi)}
          </p>
          <p className="mt-2 font-display text-lg text-[hsl(var(--ink))]">{q.q}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {q.options.map((opt) => {
              const active = answers[q.id] === opt.score;
              return (
                <button
                  key={opt.label}
                  type="button"
                  data-testid={`q-${q.id}-${opt.score}`}
                  onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.score }))}
                  className={`rounded-full border px-4 py-2 text-sm transition-all ${
                    active
                      ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))] text-[hsl(var(--background))]"
                      : "border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--ink))] hover:border-[hsl(var(--forest))]"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="rounded-2xl border border-[hsl(var(--forest))]/20 bg-[hsl(var(--forest))]/5 p-6">
        {recommendation ? (
          <>
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--forest))]">
              {resultEyebrow}
            </p>
            <p data-testid="recommendation-title" className="mt-2 font-display text-2xl text-[hsl(var(--ink))]">
              {recommendation.title}
            </p>
            <p className="mt-3 text-[hsl(var(--ink-soft))]">{recommendation.body}</p>
            <a
              data-testid="recommendation-cta"
              href="#start"
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--forest))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--background))] hover:-translate-y-0.5 hover:bg-[hsl(var(--forest-dark))]"
            >
              {recommendation.cta} <ArrowRight className="h-4 w-4" />
            </a>
          </>
        ) : (
          <p className="text-sm text-[hsl(var(--ink-soft))]">
            {prompt}
          </p>
        )}
      </div>
    </div>
  );
};

export const TargetGroupCard = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5">
    <Icon className="h-5 w-5 text-[hsl(var(--forest))]" strokeWidth={1.7} />
    <p className="mt-3 font-display text-base text-[hsl(var(--ink))]">{title}</p>
    <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">{body}</p>
  </div>
);

export const CriterionBlock = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6">
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--forest))]/10">
      <Icon className="h-4 w-4 text-[hsl(var(--forest))]" strokeWidth={1.8} />
    </div>
    <h3 className="mt-4 font-display text-lg text-[hsl(var(--ink))]">{title}</h3>
    <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">{body}</p>
  </div>
);

export const BeforeAfterTable = ({ rows, title, titleAfter }) => (
  <div className="mt-10 overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
    <table className="w-full text-left text-sm">
      <thead className="bg-[hsl(var(--surface-secondary))]">
        <tr>
          <th className="px-5 py-3 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ink-soft))]">
            {title || "Vandaag"}
          </th>
          <th className="px-5 py-3 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--forest))]">
            {titleAfter || "Met EquiSight"}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.before} className="border-t border-[hsl(var(--border))]">
            <td className="px-5 py-4 text-[hsl(var(--ink-soft))]">{row.before}</td>
            <td className="px-5 py-4 text-[hsl(var(--ink))]">{row.after}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const PainPointsGrid = ({ items }) => (
  <div className="mt-10 grid gap-3 sm:grid-cols-2">
    {items.map((q) => (
      <div
        key={q}
        className="flex items-start gap-2 rounded-xl bg-[hsl(var(--background))] p-4 text-sm text-[hsl(var(--ink-soft))]"
      >
        <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sand-dark))]" strokeWidth={1.7} />
        <span>{q}</span>
      </div>
    ))}
  </div>
);

export const CheckList = ({ items }) => (
  <div className="mt-10 grid gap-3 sm:grid-cols-2">
    {items.map((line) => (
      <div
        key={line}
        className="flex items-start gap-2 rounded-xl bg-[hsl(var(--surface))] p-4 text-sm text-[hsl(var(--ink))]"
      >
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--forest))]" strokeWidth={2.4} />
        <span>{line}</span>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// CornerstonePage — top-level orchestrator. Accepts a fully-typed config:
//
// {
//   path, seo:{title,description,keywords,priority?},
//   hero:{eyebrow,title,intro,pills,ctas:[{label,href,primary?}]},
//   pains:{title,lead,items},
//   what:{title,lead,targetGroups:[{icon,title,body}]},
//   criteria:{title,lead,items:[{icon,title,body}]},
//   comparison:{title,lead,competitors,rows,note},
//   why:{title,lead,points:[{icon,title,body}]},
//   nicheA:{title,lead,items,ctaLabel,ctaHref}, // optional
//   nicheB:{title,lead,cards:[{t,d}],ctaLabel,ctaHref}, // optional
//   beforeAfter:{title,lead,rows},
//   helper:{title,lead,questions,recommend},
//   faq:[{q,a}]
// }
// ---------------------------------------------------------------------------
export const CornerstonePage = ({ config, ui }) => (
  <main data-testid={`page-${config.path.replace(/\//g, "")}`} className="bg-background">
    <Seo
      title={config.seo.title}
      description={config.seo.description}
      path={config.path}
      keywords={config.seo.keywords}
      type="article"
      breadcrumbs={[
        { name: "EquiSight", path: "/" },
        { name: config.hero.breadcrumbLabel || config.hero.eyebrow, path: config.path },
      ]}
      faq={config.faq}
    />
    <AnnouncementBar />
    <Header />
    <UtmBanner />
    <Breadcrumbs
      items={[
        { name: "EquiSight", path: "/" },
        { name: config.hero.breadcrumbLabel || config.hero.eyebrow, path: config.path },
      ]}
    />

    {/* HERO */}
    <section className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
      <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
        {config.hero.eyebrow}
      </p>
      <h1
        data-testid="page-h1"
        className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] sm:text-5xl lg:text-6xl"
      >
        {config.hero.title}
      </h1>
      <p className="mt-6 max-w-3xl text-base text-[hsl(var(--ink-soft))] sm:text-lg">
        {config.hero.intro}
      </p>
      {config.hero.pills && (
        <div className="mt-8 flex flex-wrap gap-2">
          {config.hero.pills.map((p) => (
            <Pill key={p.label} icon={p.icon} label={p.label} />
          ))}
        </div>
      )}
      <div className="mt-10 flex flex-wrap gap-3">
        {config.hero.ctas.map((cta, i) => (
          <a
            key={cta.label}
            data-testid={`hero-cta-${i}`}
            href={cta.href}
            className={`inline-flex items-center gap-1.5 rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5 ${
              cta.primary
                ? "bg-[hsl(var(--forest))] text-[hsl(var(--background))] hover:bg-[hsl(var(--forest-dark))]"
                : "border border-[hsl(var(--border))] bg-[hsl(var(--surface))] text-[hsl(var(--ink))] hover:border-[hsl(var(--forest))]"
            }`}
          >
            {cta.label} {cta.primary && <ArrowRight className="h-4 w-4" />}
          </a>
        ))}
      </div>
    </section>

    {/* PAINS */}
    <section className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]">
      <div className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
        <SectionHeader eyebrow={ui?.sectionEyebrows?.pains || "Waarom dit pijn doet"} title={config.pains.title} lead={config.pains.lead} />
        <PainPointsGrid items={config.pains.items} />
      </div>
    </section>

    {/* WHAT */}
    <section className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
      <SectionHeader eyebrow={ui?.sectionEyebrows?.basics || "Basics"} title={config.what.title} lead={config.what.lead} />
      <h3 className="mt-12 font-display text-xl text-[hsl(var(--ink))]">
        {config.what.targetGroupsTitle || ui?.targetGroupsFallback || "Voor wie is dit bedoeld?"}
      </h3>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {config.what.targetGroups.map((g) => (
          <TargetGroupCard key={g.title} icon={g.icon} title={g.title} body={g.body} />
        ))}
      </div>
    </section>

    {/* CRITERIA */}
    <section className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]">
      <div className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
        <SectionHeader eyebrow={ui?.sectionEyebrows?.criteria || "Wat maakt het goed?"} title={config.criteria.title} lead={config.criteria.lead} />
        <div className="mt-10 grid gap-4 lg:grid-cols-2">
          {config.criteria.items.map((it) => (
            <CriterionBlock key={it.title} icon={it.icon} title={it.title} body={it.body} />
          ))}
        </div>
      </div>
    </section>

    {/* COMPARISON */}
    <section id="vergelijking" className="mx-auto max-w-6xl px-6 py-20 lg:px-10 lg:py-28">
      <SectionHeader eyebrow={ui?.sectionEyebrows?.comparison || "Eerlijke vergelijking"} title={config.comparison.title} lead={config.comparison.lead} />
      <ComparisonTable
        competitors={config.comparison.competitors}
        rows={config.comparison.rows}
        note={config.comparison.note}
        featureLabel={ui?.table?.feature || "Functie"}
      />
    </section>

    {/* WHY */}
    <section className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]">
      <div className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
        <SectionHeader eyebrow={ui?.sectionEyebrows?.why || "Het verschil"} title={config.why.title} lead={config.why.lead} />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {config.why.points.map((p) => (
            <div key={p.title} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-6">
              <p.icon className="h-5 w-5 text-[hsl(var(--forest))]" strokeWidth={1.7} />
              <h3 className="mt-4 font-display text-lg text-[hsl(var(--ink))]">{p.title}</h3>
              <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* NICHE A (checklist style) */}
    {config.nicheA && (
      <section className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
        <SectionHeader eyebrow={config.nicheA.eyebrow} title={config.nicheA.title} lead={config.nicheA.lead} />
        <CheckList items={config.nicheA.items} />
        {config.nicheA.ctaLabel && (
          <a
            href={config.nicheA.ctaHref}
            className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-5 py-2.5 text-sm text-[hsl(var(--ink))] hover:border-[hsl(var(--forest))]"
          >
            {config.nicheA.ctaLabel} <ArrowRight className="h-4 w-4" />
          </a>
        )}
      </section>
    )}

    {/* NICHE B (card grid style) */}
    {config.nicheB && (
      <section className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]">
        <div className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
          <SectionHeader eyebrow={config.nicheB.eyebrow} title={config.nicheB.title} lead={config.nicheB.lead} />
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {config.nicheB.cards.map((c) => (
              <div key={c.t} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-5">
                <p className="font-display text-base text-[hsl(var(--ink))]">{c.t}</p>
                <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">{c.d}</p>
              </div>
            ))}
          </div>
          {config.nicheB.ctaLabel && (
            <a
              href={config.nicheB.ctaHref}
              className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-5 py-2.5 text-sm text-[hsl(var(--ink))] hover:border-[hsl(var(--forest))]"
            >
              {config.nicheB.ctaLabel} <ArrowRight className="h-4 w-4" />
            </a>
          )}
        </div>
      </section>
    )}

    {/* BEFORE/AFTER */}
    {config.beforeAfter && (
      <section className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
        <SectionHeader
          eyebrow={ui?.sectionEyebrows?.beforeAfter || "Van versnippering naar overzicht"}
          title={config.beforeAfter.title}
          lead={config.beforeAfter.lead}
        />
        <BeforeAfterTable rows={config.beforeAfter.rows} title={ui?.table?.today} titleAfter={ui?.table?.withEquisight} />
      </section>
    )}

    {/* HELPER */}
    <section id="keuzehulp" className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]">
      <div className="mx-auto max-w-3xl px-6 py-20 lg:px-10 lg:py-28">
        <SectionHeader eyebrow={ui?.sectionEyebrows?.helper || "5 vragen"} title={config.helper.title} lead={config.helper.lead} />
        <DecisionHelper questions={config.helper.questions} recommend={config.helper.recommend} ui={ui?.helper} />
      </div>
    </section>

    <Faq title={config.faqTitle || ui?.faqTitleFallback || "Veelgestelde vragen"} items={config.faq} />

    <div id="start">
      <FinalCTA />
    </div>

    <Footer />
  </main>
);

export default CornerstonePage;
